/**
 * InterceptorChain - Manages and executes interceptors in order
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";

export class InterceptorChain {
  private interceptors: Interceptor[] = [];

  /**
   * Add an interceptor to the chain
   */
  addInterceptor(interceptor: Interceptor): void {
    // Remove existing interceptor with same name
    this.removeInterceptor(interceptor.name);

    // Add new interceptor and sort by order
    this.interceptors.push(interceptor);
    this.interceptors.sort((a, b) => a.order - b.order);
  }

  /**
   * Remove interceptor by name
   */
  removeInterceptor(name: string): void {
    this.interceptors = this.interceptors.filter((i) => i.name !== name);
  }

  /**
   * Clear all interceptors
   */
  clearInterceptors(): void {
    this.interceptors = [];
  }

  /**
   * Get all interceptors
   */
  getInterceptors(): Interceptor[] {
    return [...this.interceptors];
  }

  /**
   * Process a message through the interceptor chain
   */
  async process(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    finalHandler: () => Promise<any>,
  ): Promise<any> {
    if (this.interceptors.length === 0) {
      return finalHandler();
    }

    let currentIndex = 0;

    const next = async (): Promise<any> => {
      if (currentIndex >= this.interceptors.length) {
        return finalHandler();
      }

      const interceptor = this.interceptors[currentIndex++];
      return interceptor.intercept(direction, message, next);
    };

    return next();
  }
}
